import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import { findDsnDefect } from '../build-env';

import { IGNORED_ERRORS, buildBeforeSend } from './sentry-config';

export interface SentryInitOptions {
  /**
   * The deploy this bundle is running on: `production`, `uat`, `qa1`,
   * `candidate`, `main` or `preview`.
   *
   * Passed in by the app rather than read here, because this package is consumed
   * as source and bundled by each app's Vite config — so a `process.env` read in
   * this file resolves against the app's `define`/`envPrefix`, not against the
   * deploy. That is exactly how it broke: the value was read from
   * `process.env.ANALYTICS_CAPTURE_ENVIRONMENT`, which no app declared, and every
   * event Sentry received carried `environment: null`.
   */
  environment?: string;
  /** Share of transactions sampled for performance. 0 disables tracing. */
  tracesSampleRate?: number;
}

/**
 * Stands in for a missing environment so events still group by deploy.
 *
 * `Sentry.init({ environment: undefined })` produces events with no environment
 * at all, which is indistinguishable from the bug this replaced — an explicit
 * marker is searchable and shows up as a gap to fix.
 */
export const UNKNOWN_ENVIRONMENT = 'unknown';

/**
 * PostHog "Product Data" dataset the Sentry link writes into.
 *
 * https://pypestream.atlassian.net/wiki/spaces/analytics/pages/3632398424/Analytics+Dogfooding+-+Implementation+Details#Sentry-Integration
 */
const POSTHOG_ORGANIZATION = 'pypestream';
const POSTHOG_DATASET_ID = 21;

export const sentryInit = (dsn: string, options: SentryInitOptions = {}) => {
  try {
    if (!dsn) {
      throw new Error('Sentry DSN is not provided');
    }

    // PSTUDIO-4260. A non-empty value that is not a DSN used to reach
    // `Sentry.init`, which refuses it internally and leaves a dead client —
    // UAT reported nothing for five months and nobody saw a line about it.
    // The predicate is SHARED with the build-time guard rather than restated,
    // so the two cannot drift into disagreeing about what a DSN is; the build
    // guard is the first line and this is the one that catches every path it
    // cannot reach (a docker build arg, a local `.env.local`, an app the guard
    // is not wired into). The message names the defect, never the value.
    const defect = findDsnDefect(dsn);

    if (defect !== null) {
      throw new Error(`Sentry DSN is unusable — ${defect}`);
    }

    Sentry.init({
      dsn,
      environment: options.environment || UNKNOWN_ENVIRONMENT,
      integrations: [
        posthog.sentryIntegration({
          organization: POSTHOG_ORGANIZATION,
          projectId: POSTHOG_DATASET_ID,
        }),
        Sentry.browserTracingIntegration(),
      ],
      // Session Replay is deliberately NOT enabled here. It was switched on once
      // (6319e3978e) and removed five days later by 3d1ec3c807, which deleted the
      // integration together with the `sentryReplay?.start()` call that gated it
      // on consent — the commit it removed carried the comment "Start replay
      // manually on consent". Turning it back on is a privacy decision, not a
      // configuration one: `Sentry.init` runs at DOMContentLoaded, long before
      // `optionalConsentStatus` arrives over GraphQL, and `Sentry.close()`
      // flushes the queue before it disables the client. Re-enable it only
      // behind a consent gate, and only after the author of that removal agrees.
      tracesSampleRate: options.tracesSampleRate ?? 0.1,
      ignoreErrors: IGNORED_ERRORS,
      beforeSend: buildBeforeSend(),
    });
    console.log(
      '%cSentry: service initialized successfully',
      'color: green; font-size: larger; font-weight: bold'
    );
  } catch (error) {
    console.error('Sentry initialization failed:', error);
  }
};
